import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useTheme } from '../../../../theme-context';
import { TRANSACTIONS_TYPE } from '../../../../utils/constants/insights-filter';
import TableActions from '../../../../shared/table/table-actions';

const VoidedTable = ({
    rows,
    columns,
    paginationData,
    paginationState,
    setPaginationState,
    sortSelected,
    handleSortChange,
    onOpenTransactionDetails,
    onClickShowTransactionModal
}: {
    rows?: any;
    columns?: any;
    paginationData?: any;
    paginationState?: any;
    sortSelected?: any;
    setPaginationState?: any
    handleSortChange?: (sortOrder: string, type: string) => void;
    onOpenTransactionDetails?: (row: any) => void;
    onClickShowTransactionModal: (row: any) => void;
}) => {
    const { theme } = useTheme();
    const statusFilterRef = useRef<any>(null);
    const statusFilterRef1 = useRef<any>(null);
    const varianceRef = useRef<any>(null);
    const posRef = useRef<any>(null);
    const pspRef = useRef<any>(null);
    const [isPspFilterOpen, setIsPspFilterOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState<any>();
    const [isPosFilterOpen, setIsPosFilterOpen] = useState(false); const [openedTransactions, setOpenedTransactions] = useState<any>();
    const [orderedColumns, setOrderedColumns] = useState(columns || []);
    const draggedColumnIndex = useRef<number | null>(null);

    useEffect(() => {
        if (!columns?.length) return;

        const savedOrder = localStorage.getItem('transactionsTableColumnOrder');
        if (savedOrder) {
            try {
                const savedKeys: string[] = JSON.parse(savedOrder);
                const savedColumns = savedKeys
                    .map((key) => columns.find((col: any) => col.key === key))
                    .filter(Boolean);

                const newColumns = columns.filter(
                    (col: any) => !savedKeys.includes(col.key)
                );

                setOrderedColumns([...savedColumns, ...newColumns]);
            } catch (e) {
                console.error('Invalid column order in localStorage. Resetting to default.');
                setOrderedColumns(columns);
            }
        } else {
            setOrderedColumns(columns);
        }
    }, [columns]);

    const onDragStart = (index: number) => {
        draggedColumnIndex.current = index;
    };

    const onDrop = (index: number) => {
        if (draggedColumnIndex.current === null || draggedColumnIndex.current === index) return;

        const newCols = [...orderedColumns];
        const [movedCol] = newCols.splice(draggedColumnIndex.current, 1);
        newCols.splice(index, 0, movedCol);

        setOrderedColumns(newCols);

        const orderedKeys = newCols.map((col) => col.key);
        localStorage.setItem('transactionsTableColumnOrder', JSON.stringify(orderedKeys));

        draggedColumnIndex.current = null;
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (statusFilterRef?.current && !statusFilterRef?.current?.contains(event.target)) {
                setIsPosFilterOpen(false);
            }
            if (statusFilterRef1?.current && !statusFilterRef1?.current?.contains(event.target)) {
                setIsPspFilterOpen(false);
            }
            if (varianceRef?.current && !varianceRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    variance: false
                }));
            }
            if (pspRef?.current && !pspRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPSP: false
                }));
            }
            if (posRef?.current && !posRef?.current?.contains(event.target)) {
                setFilterOpen((prev: any) => ({
                    ...prev || {},
                    amountPOS: false
                }));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const formatStatus = (status: string) => {
        return <div className={`flex items-center px-3 py-1 bg-[#545863] rounded-md justify-center`}>
            <p className={`text-white font-normal text-nowrap`}>Voided</p>
        </div>
    }

    const getColorAndIcon = (color: string) => {
        switch (color) {
            case 'Green':
                return { color: 'text-[#10B981]', icon: '/assets/insights-tab/matched-table.svg' }
            case 'Red':
                return { color: 'text-[#EF4444]', icon: '/assets/insights-tab/discrepancy-table.svg' }
            case 'Amber':
                return { color: 'text-[#F59E0B]', icon: '/assets/insights-tab/likely-matched-table.svg' }
        }
    }

    const onToggleTransaction = (id: string, hide: boolean) => {
        if (hide) {
            const filterTransactions = openedTransactions?.length > 0 && openedTransactions?.filter((item: string) => item !== id)
            setOpenedTransactions(filterTransactions)
        } else {
            setOpenedTransactions([...openedTransactions || [], id])
        }
    }

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';

        return text
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    return (
        <div className=''>
            <div className='overflow-x-auto max-h-[520px] overflow-y-auto border border-1 !rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-full bg-white dark:bg-black dark:text-primary-dark'>
                    <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-dark rounded-xl'>
                        <tr>
                            {orderedColumns && orderedColumns?.length > 0 && orderedColumns?.map((item: any, idx: number) => {
                                return (
                                    <th
                                        draggable
                                        data-column-index={idx}
                                        onDragStart={() => onDragStart(idx)}
                                        onDragOver={onDragOver}
                                        onDrop={() => onDrop(idx)}
                                        key={idx}
                                        className={`px-4 py-3 cursor-move ${(item?.name === 'PSP Date' || item?.name === 'POS Date') ? 'min-w-[130px]' : ''}`}>
                                        <div className='flex flex-row items-start'>
                                            <p>{item?.name}</p>
                                            {(item?.name === 'POS Date') && (
                                                <div className='relative ml-2' ref={statusFilterRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setIsPosFilterOpen(!isPosFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isPosFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'pos')}
                                                                    className={`block ${sortSelected?.pos === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'PSP Date') && (
                                                <div className='relative ml-2' ref={statusFilterRef1}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setIsPspFilterOpen(!isPspFilterOpen)}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {isPspFilterOpen && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('new', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'new' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort New to Old
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('old', 'psp')}
                                                                    className={`block ${sortSelected?.psp === 'old' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Old to New
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'Variance') && (
                                                <div className='relative ml-2' ref={varianceRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            variance: !filterOpen?.variance
                                                        }
                                                        ))}
                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.variance && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'variance')}
                                                                    className={`block ${sortSelected?.variance === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'variance')}
                                                                    className={`block ${sortSelected?.variance === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'PSP Value') && (
                                                <div className='relative ml-2' ref={pspRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPSP: !filterOpen?.amountPSP
                                                        }
                                                        ))}                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPSP && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'pspValue')}
                                                                    className={`block ${sortSelected?.pspValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(item?.name === 'POS Value') && (
                                                <div className='relative ml-2' ref={posRef}>
                                                    <div
                                                        className='cursor-pointer hover:opacity-80'
                                                        onClick={() => setFilterOpen((prev: any) => ({
                                                            ...prev || {},
                                                            amountPOS: !filterOpen?.amountPOS
                                                        }
                                                        ))}                                                    >
                                                        <img
                                                            src={theme === 'dark'
                                                                ? '/assets/table-actions/filters-white.svg'
                                                                : '/assets/table-actions/filters.svg'}
                                                            className='object-contain w-[20px]'
                                                        />
                                                    </div>
                                                    {filterOpen?.amountPOS && (
                                                        <div className='absolute top-7 left-0 z-10 w-56 rounded-md border bg-white shadow-lg p-3 text-sm space-y-3 dark:bg-black dark:border-gray-700'>
                                                            <div className='space-y-1'>
                                                                <p className='font-medium text-gray-700 dark:text-white'>Sort</p>
                                                                <button
                                                                    onClick={() => handleSortChange?.('big', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'big' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Biggest to Smallest
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSortChange?.('small', 'posValue')}
                                                                    className={`block ${sortSelected?.posValue === 'small' ? 'font-bold' : 'font-normal'} w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded`}
                                                                >
                                                                    Sort Smallest to Biggest
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                )
                            })}
                            <th className='px-4 py-3'>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows?.map((row: any) => {
                            const findifOpened = openedTransactions?.length > 0 && openedTransactions?.find((transaction: string) => transaction === row?.transactionId)
                            return (
                                <>
                                    <tr key={row.id} className={`${findifOpened ? 'bg-[#e1e8fa] dark:bg-gray-800' : 'hover:bg-gray-50 dark:hover:bg-bgColor-darkMode'}  text-sm`}>
                                        {orderedColumns && orderedColumns?.length > 0 && orderedColumns?.map((item: any, idx: number) => {
                                            if (item?.key === 'variance') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${getColorAndIcon(row?.rag)?.color}`}>€{row?.[item?.key] || 0}</td>
                                                )
                                            }
                                            if (item?.key === 'status') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{formatStatus(row?.[item?.key])}</td>
                                                )
                                            }
                                            if (item.key === 'reason') {
                                                return (
                                                    <td key={idx} className='px-4 py-4'>{formatText(row?.[item.key])}</td>
                                                );
                                            }
                                            if (item?.key === 'type') {
                                                const findTransaction = row?.[item?.key] ? TRANSACTIONS_TYPE?.find((transaction: any) => transaction?.value === row?.[item?.key])?.label : row?.[item?.key]
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>{findTransaction}</td>
                                                )
                                            }
                                            if (item?.key === 'transactionId') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>
                                                        <div
                                                            className='flex flex-row items-center'>
                                                            {(row?.voidedMatches && row?.voidedMatches?.length > 0) &&
                                                                <div
                                                                    className='cursor-pointer hover:opacity-80 min-w-[18px]'
                                                                    onClick={() => onToggleTransaction(row?.transactionId, !!findifOpened)}>
                                                                    <img src={
                                                                        theme === 'dark' ?
                                                                            findifOpened ? '/assets/table-actions/close-white.svg' : '/assets/table-actions/add-white.svg'
                                                                            : findifOpened ? '/assets/table-actions/close.svg' : '/assets/table-actions/add.svg'} className='object-contain w-[18px] mr-2' />
                                                                </div>
                                                            }
                                                            <p>{row?.transactionId}</p>
                                                        </div>
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'rag') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4`}>
                                                        <div className='flex flex-row items-center'>
                                                            <img src={getColorAndIcon(row?.rag)?.icon} className='object-contain w-[18px] mr-2' />
                                                            <p>{row?.rag}</p>
                                                        </div>
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'amountPOS' || item?.key === 'amountPSP') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p> {['amountPOS', 'amountPSP']?.includes(item?.key) && row?.[item?.key] ? '€' : ''}{row?.[item?.key]}</p>
                                                        {/* <p className='text-primary-light text-sm'>{row?.[item?.secondKey] ? moment(row?.[item?.secondKey]).format('DD/MM/YYYY hh:mm A') : ''}</p> */}
                                                    </td>
                                                )
                                            }
                                            if (item?.key === 'pspDateTime' || item?.key === 'posDateTime') {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                        <p className='text-sm'>{row?.[item?.key] ? moment(row?.[item?.key]).format('DD/MM/YYYY hh:mm A') : '--'}</p>
                                                    </td>
                                                )
                                            }
                                            else {
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${['dateTime', 'dateAnalyzed']?.includes(item?.key) ? 'min-w-[120px]' : ''} ${item?.key === 'description' ? 'text-primary-light' : ''}`}>{['amountPOS', 'amountPSP']?.includes(item?.key) ? '€' : ''}{row?.[item?.key] || '--'}</td>
                                                )
                                            }
                                        })}
                                        <td className={`px-4 py-4`}>
                                            <TableActions
                                                containerStyle='!mt-[-10px] !mr-[-20px] !rounded-sm dark:bg-gray-800'>
                                                <div
                                                    onClick={() => onClickShowTransactionModal(row)}
                                                    className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                    <p className='dark:text-primary-dark'>View details</p>
                                                </div>
                                                {(row?.voidedMatches && row?.voidedMatches?.length > 0) &&
                                                    <div
                                                        onClick={() => onOpenTransactionDetails?.(row)}
                                                        className='bg-gray-100 dark:bg-gray-800 shadow-xl px-5 py-2 cursor-pointer hover:opacity-70'>
                                                        <p className='text-[#2D8EFF]'>Review Reconciliation</p>
                                                    </div>
                                                }
                                            </TableActions>
                                        </td>
                                    </tr>
                                    {findifOpened && row?.voidedMatches?.length > 0 && row?.voidedMatches?.map((child: any, childIdx: number) => (
                                        <tr key={`${row.transactionId}-child-${childIdx}`} className={`${findifOpened ? 'bg-[#e1e8fa] dark:bg-gray-800' : 'hover:bg-gray-50 dark:hover:bg-bgColor-darkMode'} text-sm`}>
                                            {orderedColumns?.map((item: any, idx: number) => {
                                                if (item.key === 'variance') {
                                                    return (
                                                        <td key={idx} className={`px-4 py-4 ${getColorAndIcon(child?.rag)?.color}`}>
                                                            ${child?.[item.key] || 0}
                                                        </td>
                                                    );
                                                }

                                                if (item.key === 'status') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>{formatStatus(child?.[item.key])}</td>
                                                    );
                                                }
                                                if (item.key === 'reason') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>{formatText(child?.[item.key])}</td>
                                                    );
                                                }
                                                if (item?.key === 'type') {
                                                    const findTransaction = child?.[item?.key] ? TRANSACTIONS_TYPE?.find((transaction: any) => transaction?.value === child?.[item?.key])?.label : child?.[item?.key]
                                                    return (
                                                        <td key={idx} className={`px-4 py-4`}>{findTransaction}</td>
                                                    )
                                                }
                                                if (item.key === 'transactionId') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4 pl-8 text-sm text-primary-light'>
                                                            {child?.transactionId}
                                                        </td>
                                                    );
                                                }

                                                if (item.key === 'rag') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4'>
                                                            <div className='flex items-center'>
                                                                <img src={getColorAndIcon(child?.rag)?.icon} className='w-[18px] mr-2' />
                                                                <p>{child?.rag}</p>
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                if (item.key === 'amountPOS' || item.key === 'amountPSP') {
                                                    return (
                                                        <td key={idx} className='px-4 py-4 min-w-[120px]'>
                                                            <p>€{Number(child?.[item.key] || 0).toFixed(2)}</p>
                                                            {/* <p className='text-primary-light text-sm'>{row?.[item?.secondKey] ? moment(row?.[item?.secondKey]).format('DD/MM/YYYY hh:mm A') : ''}</p> */}
                                                        </td>
                                                    );
                                                }
                                                if (item?.key === 'pspDateTime' || item?.key === 'posDateTime') {
                                                    return (
                                                        <td key={idx} className={`px-4 py-4 min-w-[120px]`}>
                                                            <p className='text-sm'>{child?.[item?.key] ? moment(child?.[item?.key]).format('DD/MM/YYYY hh:mm A') : '--'}</p>
                                                        </td>
                                                    )
                                                }
                                                return (
                                                    <td key={idx} className={`px-4 py-4 ${item.key === 'description' ? 'text-primary-light' : ''}`}>
                                                        {child?.[item.key] || '--'}
                                                    </td>
                                                );
                                            })}
                                            <td className='px-4 py-4'></td>
                                        </tr>
                                    ))}
                                </>
                            )
                        })
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default VoidedTable;